import { useCallback, useEffect, useRef, useState } from 'react';
import { NetworkStatus } from '@apollo/client';
import { useAsyncBulkGenerateQuizMeMutation, useAsyncBulkGetQuizMeLazyQuery } from '../../../../__generated__/chirp/com/hubspot/academy/gen/ai/skills/rpc/QuizMeRpc.hooks';
import { checkQuizMePollingStatus, findLessonBulkPollFlatIndex, PRE_ASSESSMENT_DIFFICULTY, spliceQuizMeCache, useQuizMeSession } from '../utils/quizMeUtils';
import { handleApolloErrors } from '../../../utils/errorUtils';
const processPollingResult = ({
  contentType,
  context,
  pollData,
  quizReady,
  setQuizMeStatus,
  setQuizMeTasks,
  setDifficultyTasks
}) => {
  const taskData = pollData === null || pollData === void 0 ? void 0 : pollData.tasks[0];
  if (quizReady && taskData) {
    if (context.flatIndex !== -1) {
      const flatIndex = context.flatIndex;
      const newFullTaskIds = [...context.oldTaskIds];
      newFullTaskIds[flatIndex] = context.newTaskId;
      const updatedCache = spliceQuizMeCache({
        oldTaskIds: context.oldTaskIds,
        newTaskIds: newFullTaskIds,
        videoIndex: flatIndex,
        newTaskData: taskData
      });
      if (updatedCache) {
        if (context.difficulty) {
          setDifficultyTasks({
            contentType,
            contentId: context.contentId,
            difficultyTasks: {
              [context.difficulty]: context.newTaskId
            }
          });
          if (context.difficulty === PRE_ASSESSMENT_DIFFICULTY) {
            setQuizMeTasks({
              contentType,
              videoContentId: context.contentId,
              tasks: context.newTaskId
            });
          }
        } else {
          setQuizMeTasks({
            contentType,
            videoContentId: context.contentId,
            tasks: context.newTaskId
          });
        }
        setQuizMeStatus({
          contentType,
          contentId: context.contentId,
          quizMeStatus: taskData.status
        });
        return;
      }
    }
  }
  setQuizMeStatus({
    contentType,
    contentId: context.contentId,
    quizMeStatus: 'FAILED'
  });
};
export const useSingleVideoQuizRegeneration = ({
  contentType,
  getAllTaskIds
}) => {
  const [pollCount, setPollCount] = useState(0);
  const regenContextRef = useRef(null);
  const {
    getQuizState,
    setQuizMeTasks,
    setQuizMeStatus,
    setDifficultyTasks,
    initialiseQuiz
  } = useQuizMeSession();
  const [getQuizMeLazy, {
    data: pollData,
    loading: queryLoading,
    error: pollError,
    stopPolling,
    networkStatus
  }] = useAsyncBulkGetQuizMeLazyQuery({
    notifyOnNetworkStatusChange: true,
    pollInterval: 3000,
    onError: err => {
      if (pollCount < 2) {
        handleApolloErrors({
          error: err,
          loggingTitle: 'learningCenter.interactiveEducation.errors.asyncQuizMeGetQuizError'
        });
      }
    }
  });
  const isPolling = networkStatus === NetworkStatus.poll || queryLoading;
  const {
    quizReady,
    retryGeneration
  } = checkQuizMePollingStatus(pollData, !!pollError);
  const pollEffectRef = useRef({
    quizReady,
    retryGeneration,
    pollCount,
    contentType,
    setQuizMeTasks,
    setQuizMeStatus,
    setDifficultyTasks
  });
  pollEffectRef.current = {
    quizReady,
    retryGeneration,
    pollCount,
    contentType,
    setQuizMeTasks,
    setQuizMeStatus,
    setDifficultyTasks
  };
  useEffect(() => {
    const context = regenContextRef.current;
    if (!context) {
      return;
    }
    const {
      quizReady: isQuizReady,
      retryGeneration: shouldRetry,
      pollCount: currentPollCount,
      contentType: currentContentType,
      setQuizMeTasks: updateQuizMeTasks,
      setQuizMeStatus: updateQuizMeStatus,
      setDifficultyTasks: updateDifficultyTasks
    } = pollEffectRef.current;
    if (!isQuizReady && isPolling) {
      setPollCount(prev => prev + 1);
    }
    if (currentPollCount > 20 || isQuizReady || shouldRetry) {
      stopPolling();
      setPollCount(0);
      processPollingResult({
        pollData,
        quizReady: isQuizReady,
        context,
        contentType: currentContentType,
        setQuizMeTasks: updateQuizMeTasks,
        setQuizMeStatus: updateQuizMeStatus,
        setDifficultyTasks: updateDifficultyTasks
      });
      regenContextRef.current = null;
    }
  }, [stopPolling, pollData, pollError, isPolling]);
  const [asyncGenerateQuizMe] = useAsyncBulkGenerateQuizMeMutation({
    onError: err => handleApolloErrors({
      error: err,
      loggingTitle: 'learningCenter.interactiveEducation.errors.asyncQuizGenerationError'
    }),
    onCompleted: quizData => {
      const generateData = quizData.result;
      const newTask = generateData === null || generateData === void 0 ? void 0 : generateData.tasks[0];
      const context = regenContextRef.current;
      if (!context) {
        return;
      }
      if (!newTask) {
        setQuizMeStatus({
          contentType,
          contentId: context.contentId,
          quizMeStatus: 'FAILED'
        });
        regenContextRef.current = null;
        return;
      }
      regenContextRef.current = Object.assign({}, context, {
        newTaskId: newTask.taskId
      });
      initialiseQuiz({
        contentType,
        contentId: context.contentId
      });
      void getQuizMeLazy({
        variables: {
          taskIds: [newTask.taskId]
        }
      });
    }
  });
  const regenerateSingleVideo = useCallback(({
    videoRequest,
    contentId,
    difficulty
  }) => {
    if (regenContextRef.current) {
      return;
    }
    const {
      taskIds,
      contentIds
    } = getAllTaskIds();
    const effectiveDifficulty = difficulty !== null && difficulty !== void 0 ? difficulty : PRE_ASSESSMENT_DIFFICULTY;
    const flatIndex = findLessonBulkPollFlatIndex(contentIds, contentId, effectiveDifficulty, id => getQuizState(id, contentType));
    if (flatIndex === -1) return;
    setQuizMeStatus({
      contentType,
      contentId,
      quizMeStatus: 'PROCESSING'
    });
    regenContextRef.current = {
      contentId,
      flatIndex,
      oldTaskIds: taskIds,
      contentIds,
      newTaskId: '',
      difficulty
    };
    const request = difficulty ? Object.assign({}, videoRequest, {
      difficulty
    }) : videoRequest;
    void asyncGenerateQuizMe({
      variables: {
        videos: [request]
      }
    });
  }, [getAllTaskIds, asyncGenerateQuizMe, contentType, getQuizState, setQuizMeStatus]);
  return {
    regenerateSingleVideo
  };
};